import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { BillProcessingStatus } from '../../../domain/customer-utility-bill/interface';
import { useCustomerUtilityBillFileCustomersQuery, useCustomerUtilityBillFileSourcesQuery } from '../../../domain/customer-utility-bill-file/queries';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../domain/saved-filters/queries';
import { usePageLocation } from '../../usePageState';
import { checkBillFilesFilterInvalidValues, countNotEmptyFilters } from './utils';

export interface IBillFilesPageState {
    // Filters
    filter?: {
        sources?: string | string[];
        customers?: string | string[];
        statuses?: string | string[];
    };

    // Used by whole page
    companyId?: number;
    search?: string;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface IBillFilesSidebarFilters {
    sources?: string[];
    customers?: string[];
    statuses?: string[];
}

export interface IBillFilesSidebarFiltersContext {
    filtersCount: number;
    filters: IBillFilesSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const BillFilesSidebarFiltersContext = React.createContext<IBillFilesSidebarFiltersContext>({} as IBillFilesSidebarFiltersContext);

// Files tab opens on files that need attention; applied by the view switcher when switching to the Files tab.
export const DEFAULT_BILL_FILES_FILTERS: IBillFilesSidebarFilters = { statuses: [BillProcessingStatus.ParsingFailed] };

const toArray = (value: string | string[]): string[] => (Array.isArray(value) ? value : [value]);

export const convertBillFilesPageStateToFilterState = (pageState: IBillFilesPageState): IBillFilesSidebarFilters => {
    const result: IBillFilesSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert sources
    if (filterData.sources !== undefined) {
        result.sources = toArray(filterData.sources);
    }

    // Convert customers
    if (filterData.customers !== undefined) {
        result.customers = toArray(filterData.customers);
    }

    // Convert statuses
    if (filterData.statuses !== undefined) {
        result.statuses = toArray(filterData.statuses);
    }

    return result;
};

export const BillFilesSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useBillFilesSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <BillFilesSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </BillFilesSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation({ parseNumbers: false });
    const pageState: IBillFilesPageState = queryToState(location.search);

    const [filters, setFilters] = useState<IBillFilesSidebarFilters>(convertBillFilesPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: IBillFilesSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const useBillFilesSavedFilters = () => {
    const { notification } = App.useApp();
    const { data: sources = [] } = useCustomerUtilityBillFileSourcesQuery();
    const { data: customers = [] } = useCustomerUtilityBillFileCustomersQuery();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.customerUtilityBillFiles);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            const outdatedFields = checkBillFilesFilterInvalidValues(filter.filter_values, { sources, customers });
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [sources, customers]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: ISavedFilter) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useBillFilesSidebarFiltersContext = () => {
    const context = React.useContext(BillFilesSidebarFiltersContext);
    if (!context) {
        throw new Error('useBillFilesSidebarFiltersContext must be used within BillFilesSidebarFiltersContext');
    }

    return context;
};
